"use client";

import { Core } from "@faire/design-tokens";
import { useHeaderSignUpCopy } from "@faire/retailer-visitor-shared/components/Header/__internal__/useHeaderSignUpCopy";
import { useNavigateAndHideMenu } from "@faire/retailer-visitor-shared/components/Header/mobile/Navigation/useNavigateAndHideMenu";
import { HeaderButton } from "@faire/retailer-visitor-shared/components/Header/styled";
import { useIsLoggedInRetailer } from "@faire/retailer-visitor-shared/lib/isLoggedInRetailer";
import { SignUpType } from "@faire/retailer-visitor-shared/lib/popSignUp";
import { useSettingMobileLoggedOutNavUpdate } from "@faire/retailer-visitor-shared/settings/useSettingLoggedOutNavUpdate";
import { getCurrentPath } from "@faire/web--source/common/Path";
import { useIsSSR } from "@faire/web--source/common/server-side-rendering/isSSR";
import { Button } from "@faire/web--source/slate/Button";
import { Account } from "@faire/web--source/slate/icons/foundation/Account";
import { CartEmpty } from "@faire/web--source/slate/icons/foundation/CartEmpty";
import { IFourVariantXP } from "@faire/web-api--source/indigofair/settings/IFourVariantXP";
import React from "react";

export const HeaderMobileSignUp: React.FC = () => {
  const isSSR = useIsSSR();
  const isLoggedInRetailer = useIsLoggedInRetailer();
  const { navigateToSignUp } = useNavigateAndHideMenu();
  const signUpCopy = useHeaderSignUpCopy();
  const isMobileLoggedOutNavUpdate = useSettingMobileLoggedOutNavUpdate();

  const getLinkTo = (signUpType: SignUpType) => {
    if (isSSR) {
      return getCurrentPath()
        .replace({
          signUp: signUpType,
        })
        .toString();
    }
    return undefined;
  };

  if (isLoggedInRetailer) {
    return null;
  }

  switch (isMobileLoggedOutNavUpdate) {
    case IFourVariantXP.Bucket.VARIANT_2:
      return <ShoppingCartIconButton linkTo={getLinkTo(SignUpType.NAV_CART)} />;
    case IFourVariantXP.Bucket.VARIANT_3:
      return <AccountIconButton linkTo={getLinkTo(SignUpType.NAV_ACCOUNT)} />;
    case IFourVariantXP.Bucket.VARIANT_4:
      return (
        <>
          <AccountIconButton linkTo={getLinkTo(SignUpType.NAV_ACCOUNT)} />
          <ShoppingCartIconButton linkTo={getLinkTo(SignUpType.NAV_CART)} />
        </>
      );
    case IFourVariantXP.Bucket.CONTROL:
    default:
      return (
        <HeaderButton
          onClick={() =>
            navigateToSignUp(undefined, {
              sourceId: "mobile_side_catnav_signup_cta",
            })
          }
          // We set a link for graceful degredation in SSR.
          linkTo={getLinkTo(SignUpType.GENERIC)}
          data-test-id="signUpButton"
        >
          {signUpCopy}
        </HeaderButton>
      );
  }
};

const AccountIconButton = ({ linkTo }: { linkTo: string | undefined }) => {
  const { navigateToSignUp } = useNavigateAndHideMenu();
  return (
    <Button
      variant="tertiary"
      onClick={() =>
        navigateToSignUp(
          undefined,
          {
            sourceId: "mobile_side_catnav_signup_account_icon",
          },
          SignUpType.NAV_ACCOUNT
        )
      }
      linkTo={linkTo}
      data-test-id="signUpAccountIcon"
    >
      <Account
        color={Core.icon.default}
        margin="0 20px 0 0"
        height="20px"
        width="20px"
      />
    </Button>
  );
};

const ShoppingCartIconButton = ({ linkTo }: { linkTo: string | undefined }) => {
  const { navigateToSignUp } = useNavigateAndHideMenu();
  return (
    <Button
      variant="tertiary"
      onClick={() =>
        navigateToSignUp(
          undefined,
          {
            sourceId: "mobile_side_catnav_signup_shopping_cart_icon",
          },
          SignUpType.NAV_CART
        )
      }
      linkTo={linkTo}
      data-test-id="signUpShoppingCartIcon"
    >
      <CartEmpty
        margin="0 20px 0 0"
        color={Core.icon.default}
        height="20px"
        width="20px"
      />
    </Button>
  );
};
